import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Search, Copy, Check, Trash2, Pencil, KeyRound, Layers, Activity, UserRound } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/admin/AdminGate";
import { SectionHeading } from "@/components/SectionHeading";
import {
  getAccessCodes,
  createAccessCode,
  createAccessCodesBatch,
  updateAccessCode,
  deleteAccessCode,
  getAccessCodeUsage,
  downloadCsv,
} from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const Container = ({ children }) => (
  <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">{children}</div>
);

const STATUSES = ["active", "used", "disabled", "refunded"];

const STATUS_STYLES = {
  active: "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]",
  used: "bg-[color:rgba(37,99,235,0.12)] text-[#2563eb]",
  disabled: "bg-[color:rgba(11,18,32,0.1)] text-[color:rgba(11,18,32,0.6)]",
  refunded: "bg-[color:rgba(220,38,38,0.12)] text-[#dc2626]",
};

const inputCls =
  "h-10 rounded-lg border border-[color:rgba(10,27,51,0.2)] bg-white px-3 text-sm text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

function CopyCode({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      onClick={copy}
      title="Copy code"
      className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 font-mono text-xs hover:bg-[color:rgba(10,27,51,0.06)]"
    >
      {value}
      {copied ? <Check className="h-3.5 w-3.5 text-[#15803d]" /> : <Copy className="h-3.5 w-3.5 opacity-50" />}
    </button>
  );
}

function CreatePanel({ onDone }) {
  const blank = {
    access_code: "",
    customer_first_name: "",
    customer_email: "",
    shopify_order_id: "",
    status: "active",
    purchase_source: "manual",
    expires_at: "",
    notes: "",
    is_test: false,
  };
  const [form, setForm] = useState(blank);
  const [batchCount, setBatchCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitSingle = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : "",
      };
      const res = await createAccessCode(payload);
      toast.success("Access code created", { description: res.access_code });
      setForm(blank);
      onDone();
    } catch (e) {
      toast.error("Could not create code", {
        description: e?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const submitBatch = async () => {
    setBusy(true);
    try {
      const payload = {
        count: Number(batchCount) || 1,
        purchase_source: form.purchase_source || "manual",
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : "",
        notes: form.notes,
        is_test: form.is_test,
      };
      const res = await createAccessCodesBatch(payload);
      toast.success(`Created ${res.created} access code(s)`);
      onDone();
    } catch (e) {
      toast.error("Batch creation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]">
        <Plus className="h-4 w-4" /> Create access codes
      </h3>
      <p className="mt-1 text-xs text-[color:rgba(11,18,32,0.55)]">
        Codes are generated randomly when no custom code is provided. Leave expiration blank for no expiration.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Custom code (optional)
          <input className={inputCls} placeholder="auto-generate" value={form.access_code} onChange={(e) => set("access_code", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          First name
          <input className={inputCls} placeholder="Alex" value={form.customer_first_name} onChange={(e) => set("customer_first_name", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Customer email
          <input className={inputCls} placeholder="buyer@example.com" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Checkout order ID
          <input className={inputCls} placeholder="#1001" value={form.shopify_order_id} onChange={(e) => set("shopify_order_id", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Status
          <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Purchase source
          <input className={inputCls} placeholder="checkout / manual" value={form.purchase_source} onChange={(e) => set("purchase_source", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Expires at (optional)
          <input type="datetime-local" className={inputCls} value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)] sm:col-span-2 lg:col-span-3">
          Notes
          <input className={inputCls} placeholder="Internal notes (not shown to buyer)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </label>
      </div>

      <label className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-[color:rgba(11,18,32,0.7)]">
        <input type="checkbox" checked={form.is_test} onChange={(e) => set("is_test", e.target.checked)} />
        Mark as test code (only test codes can be deleted)
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          data-testid={STUDY.adminAccessCodeCreate}
          disabled={busy}
          onClick={submitSingle}
          className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          <KeyRound className="h-4 w-4" /> Create single code
        </button>

        <div className="flex items-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.15)] px-3 py-1.5">
          <Layers className="h-4 w-4 opacity-60" />
          <input
            type="number"
            min={1}
            max={500}
            value={batchCount}
            onChange={(e) => setBatchCount(e.target.value)}
            className="h-8 w-16 rounded-md border border-[color:rgba(10,27,51,0.2)] px-2 text-sm"
          />
          <button
            data-testid={STUDY.adminAccessCodeBatch}
            disabled={busy}
            onClick={submitBatch}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--ibp-gold-500)] px-4 py-1.5 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[var(--ibp-primary-hover)] disabled:opacity-50"
          >
            Generate batch
          </button>
        </div>
      </div>
    </div>
  );
}

function EditRow({ row, onSaved }) {
  const [form, setForm] = useState({
    customer_first_name: row.customer_first_name || "",
    customer_email: row.customer_email || "",
    shopify_order_id: row.shopify_order_id || "",
    purchase_source: row.purchase_source || "",
    expires_at: row.expires_at ? row.expires_at.slice(0, 16) : "",
    notes: row.notes || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      await updateAccessCode(row.code_id, {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : "",
      });
      toast.success("Updated");
      onSaved();
    } catch (e) {
      toast.error("Update failed");
    }
  };
  return (
    <tr className="border-t border-[color:rgba(10,27,51,0.08)] bg-[var(--ibp-offwhite)]">
      <td colSpan={9} className="px-4 py-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <input className={inputCls} placeholder="First name" value={form.customer_first_name} onChange={(e) => set("customer_first_name", e.target.value)} />
          <input className={inputCls} placeholder="Customer email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
          <input className={inputCls} placeholder="Checkout order ID" value={form.shopify_order_id} onChange={(e) => set("shopify_order_id", e.target.value)} />
          <input className={inputCls} placeholder="Purchase source" value={form.purchase_source} onChange={(e) => set("purchase_source", e.target.value)} />
          <input type="datetime-local" className={inputCls} value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} />
          <input className={`${inputCls} sm:col-span-2`} placeholder="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
        <button onClick={save} className="mt-3 rounded-lg bg-[var(--ibp-navy-950)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
          Save changes
        </button>
      </td>
    </tr>
  );
}

function UsageDetailRow({ codeId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getAccessCodeUsage(codeId)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setErr(true));
    return () => {
      cancelled = true;
    };
  }, [codeId]);
  const Stat = ({ label, value, warn }) => (
    <div className="rounded-lg border border-[color:rgba(10,27,51,0.10)] bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">{label}</div>
      <div className={["text-sm font-semibold", warn ? "text-[#B42318]" : "text-[var(--ibp-ink)]"].join(" ")}>{value}</div>
    </div>
  );
  return (
    <tr className="bg-[var(--ibp-offwhite)]">
      <td colSpan={9} className="px-4 py-4">
        {err ? (
          <p className="text-sm text-[#B42318]">Could not load usage.</p>
        ) : !data ? (
          <p className="text-sm text-[color:rgba(11,18,32,0.5)]">Loading usage…</p>
        ) : (
          <div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
              <Stat label="Logins" value={data.logins} />
              <Stat label="Distinct IPs" value={data.distinct_ips} warn={data.distinct_ips > 3} />
              <Stat label="Sessions" value={data.active_sessions} />
              <Stat label="Sprint attempts" value={data.sprint_attempts} />
              <Stat label="Questions viewed" value={data.questions_viewed} />
              <Stat label="Last IP" value={data.last_ip || "—"} />
            </div>
            {data.recent_ips?.length > 0 && (
              <p className="mt-3 text-xs text-[color:rgba(11,18,32,0.6)]">
                <span className="font-semibold">Recent IPs:</span> {data.recent_ips.join(", ")}
              </p>
            )}
            <p className="mt-2 text-[11px] text-[color:rgba(11,18,32,0.5)]">
              Many distinct IPs/sessions can indicate code sharing. Review and disable manually if abuse is obvious.
            </p>
          </div>
        )}
      </td>
    </tr>
  );
}

function CodesTable({ rows, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [usageOpen, setUsageOpen] = useState(null);

  const changeStatus = async (row, status) => {
    try {
      await updateAccessCode(row.code_id, { status });
      onChanged();
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete test code ${row.access_code}? This cannot be undone.`)) return;
    try {
      await deleteAccessCode(row.code_id);
      toast.success("Test code deleted");
      onChanged();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || "" });
    }
  };

  return (
    <div className="mt-4 overflow-x-auto rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white">
      <table className="w-full text-left text-sm">
        <thead className="bg-[var(--ibp-offwhite)] text-xs uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
          <tr>
            <th className="px-4 py-3">Code</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Checkout order</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Last login</th>
            <th className="px-4 py-3">Uses</th>
            <th className="px-4 py-3">IPs</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <React.Fragment key={c.code_id}>
              <tr data-testid={STUDY.adminAccessCodeRow} className="border-t border-[color:rgba(10,27,51,0.08)] align-top">
                <td className="px-4 py-3">
                  <CopyCode value={c.access_code} />
                  {c.is_test && (
                    <span className="ml-1 rounded bg-[color:rgba(212,160,23,0.18)] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#9a6b00]">test</span>
                  )}
                  {c.expires_at && (
                    <div className="text-[10px] text-[color:rgba(11,18,32,0.5)]">expires {fmtDate(c.expires_at)}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-[color:rgba(11,18,32,0.8)]">
                  {c.customer_first_name ? (
                    <div className="font-medium text-[var(--ibp-ink)]">{c.customer_first_name}</div>
                  ) : null}
                  <div className={c.customer_first_name ? "text-xs text-[color:rgba(11,18,32,0.55)]" : ""}>
                    {c.customer_email || "—"}
                  </div>
                </td>
                <td className="px-4 py-3 text-[color:rgba(11,18,32,0.8)]">{c.shopify_order_id || "—"}</td>
                <td className="px-4 py-3">
                  <select
                    value={c.status}
                    onChange={(e) => changeStatus(c, e.target.value)}
                    className={["rounded-full border-0 px-2.5 py-1 text-xs font-semibold", STATUS_STYLES[c.status] || ""].join(" ")}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-[color:rgba(11,18,32,0.6)]">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3 text-xs text-[color:rgba(11,18,32,0.6)]">{fmtDate(c.last_login_at)}</td>
                <td className="px-4 py-3 text-center font-semibold">{c.usage_count}</td>
                <td className="px-4 py-3 text-center">
                  <span
                    title={c.last_ip ? `Last IP: ${c.last_ip}` : "No logins yet"}
                    className={[
                      "inline-block min-w-[1.6rem] rounded px-1.5 py-0.5 text-xs font-semibold",
                      (c.distinct_ips || 0) > 3
                        ? "bg-[#fde8e6] text-[#B42318]"
                        : "text-[color:rgba(11,18,32,0.7)]",
                    ].join(" ")}
                  >
                    {c.distinct_ips || 0}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => setUsageOpen(usageOpen === c.code_id ? null : c.code_id)}
                      title="View usage / abuse signals"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[color:rgba(10,27,51,0.15)] hover:bg-[color:rgba(10,27,51,0.05)]"
                    >
                      <Activity className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setEditing(editing === c.code_id ? null : c.code_id)}
                      title="Edit details"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[color:rgba(10,27,51,0.15)] hover:bg-[color:rgba(10,27,51,0.05)]"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {c.status === "active" ? (
                      <button
                        onClick={() => changeStatus(c, "disabled")}
                        className="rounded-lg border border-[color:rgba(10,27,51,0.15)] px-2.5 py-1.5 text-xs font-semibold hover:bg-[color:rgba(10,27,51,0.05)]"
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        onClick={() => changeStatus(c, "active")}
                        className="rounded-lg border border-[color:rgba(21,128,61,0.3)] px-2.5 py-1.5 text-xs font-semibold text-[#15803d] hover:bg-[color:rgba(21,128,61,0.08)]"
                      >
                        Enable
                      </button>
                    )}
                    {c.is_test && (
                      <button
                        onClick={() => remove(c)}
                        title="Delete test code"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[color:rgba(220,38,38,0.3)] text-[#dc2626] hover:bg-[color:rgba(220,38,38,0.08)]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {editing === c.code_id && <EditRow row={c} onSaved={() => { setEditing(null); onChanged(); }} />}
            </React.Fragment>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-10 text-center text-sm text-[color:rgba(11,18,32,0.5)]">
                No access codes match your filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AccessCodesManager() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ q: "", status: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState(filters);

  const { data: codes = [], isLoading } = useQuery({
    queryKey: ["admin-access-codes", applied],
    queryFn: () => getAccessCodes(applied),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-access-codes"] });

  const counts = useMemo(() => {
    const c = { active: 0, used: 0, disabled: 0, refunded: 0 };
    codes.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, [codes]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const applyFilters = () => setApplied(filters);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-[var(--ibp-ink)]">Accounts are now the primary customer access system.</p>
          <p className="mt-1 text-xs leading-5 text-[color:rgba(11,18,32,0.6)]">Use this page for legacy recovery codes and manual buyer migration.</p>
        </div>
        <Link to="/admin/students" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-[var(--ibp-navy-950)] px-4 py-2 text-sm font-semibold text-white">
          <UserRound className="h-4 w-4" /> View student accounts
        </Link>
      </div>
      <CreatePanel onDone={refresh} />

      <div className="mt-8 flex flex-wrap items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Search (code / email / order)
          <input
            data-testid={STUDY.adminAccessCodeSearch}
            className={`${inputCls} min-w-[220px]`}
            value={filters.q}
            onChange={(e) => set("q", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            placeholder="Search..."
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          Status
          <select className={inputCls} value={filters.status} onChange={(e) => set("status", e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          From
          <input type="date" className={inputCls} value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
          To
          <input type="date" className={inputCls} value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} />
        </label>
        <button onClick={applyFilters} className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-[var(--ibp-navy-950)] px-4 text-sm font-semibold text-white hover:opacity-90">
          <Search className="h-4 w-4" /> Filter
        </button>
        <button
          onClick={() => downloadCsv("/admin/access-codes/export", "access_codes.csv")}
          className="ml-auto inline-flex h-10 items-center gap-1.5 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-4 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <p className="mt-4 text-sm font-medium text-[color:rgba(11,18,32,0.6)]">
        {isLoading
          ? "Loading..."
          : `${codes.length} code(s) — ${counts.active} active · ${counts.used} used · ${counts.disabled} disabled · ${counts.refunded} refunded`}
      </p>

      <CodesTable rows={codes} onChanged={refresh} />
    </div>
  );
}

export default function AdminAccessCodes() {
  return (
    <div data-testid={STUDY.adminAccessCodesPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Legacy access codes"
            subtitle="Recovery and migration tools for customers who bought before student accounts became the primary sign-in system."
          />
        </Container>
      </section>
      <section className="py-12 sm:py-16">
        <Container>
          <AdminGate>
            <AccessCodesManager />
          </AdminGate>
        </Container>
      </section>
    </div>
  );
}
